import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Loader2, Lock, Mail, User, ArrowRight, Calendar, MapPin, Users, ClipboardPaste } from 'lucide-react';
// TurnstileWidget removed — bot protection moved to Cloudflare WAF edge.
import { useClipboardCode } from '@/lib/useClipboardCode';

const ANIM_SCALE_IN = { initial: { opacity: 0, scale: 0.95 }, animate: { opacity: 1, scale: 1 } };
const ANIM_SLIDE_IN = { initial: { opacity: 0, x: 50 }, animate: { opacity: 1, x: 0 } };
const ANIM_ICON_IN = { initial: { opacity: 0, scale: 0.8 }, animate: { opacity: 1, scale: 1 } };
const EXIT_LEFT = { opacity: 0, x: -50 };
const TRANSITION_STEP = { duration: 0.3 };
const TRANSITION_FORM = { duration: 0.4 };
const TRANSITION_FORM_DELAYED = { delay: 0.1, duration: 0.4 };
const TRANSITION_ENTER = { duration: 0.5, ease: 'easeOut' };
const TRANSITION_ENTER_DELAYED = { delay: 0.2, duration: 0.5 };

export function CodeStep({ accessCode, setAccessCode, loading, onSubmit, isAuthenticated, navigate, setValidated }) {
  const { paste, supported: clipboardSupported, pasting } = useClipboardCode((code) => {
    setAccessCode(code.slice(0, 6));
  });
  return (
    <motion.div key="code-entry" {...ANIM_SCALE_IN} exit={EXIT_LEFT} transition={TRANSITION_STEP}>
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="relative">
          <Input
            value={accessCode} onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
            placeholder="ACCESS CODE" maxLength={6}
            autoComplete="one-time-code"
            inputMode="text"
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck="false"
            className="h-14 bg-card border-border text-foreground text-center font-numeric text-2xl tracking-[0.35em] uppercase placeholder:text-muted-foreground placeholder:text-base placeholder:tracking-[0.2em] focus-visible:ring-primary/50"
            data-testid="gatekeeper-access-code-input"
          />
        </div>
        {clipboardSupported && !accessCode && (
          <button
            type="button"
            onClick={paste}
            disabled={pasting}
            className="w-full flex items-center justify-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-1 disabled:opacity-50"
            data-testid="gatekeeper-paste-code-btn"
          >
            <ClipboardPaste className="w-3.5 h-3.5" />
            {pasting ? 'Pasting…' : 'Paste code from clipboard'}
          </button>
        )}
        <Button type="submit" disabled={loading || accessCode.length < 4}
          className="w-full h-12 bg-primary hover:opacity-90 text-white font-semibold text-sm transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="gatekeeper-join-button">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Enter Tournament'}
        </Button>
      </form>

      {!isAuthenticated && (
        <div className="mt-4 text-center">
          <button onClick={() => navigate('/login?tab=player')}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            data-testid="gatekeeper-signin-link">
            Already have an account? <span className="text-teal-600">Sign in</span>
          </button>
        </div>
      )}
    </motion.div>
  );
}

function SocietyBrandingBadge({ room, transition, testId }) {
  if (!room) return null;
  return (
    <motion.div {...ANIM_ICON_IN} transition={transition}
      className="mb-5 p-4 rounded-2xl bg-card/80 border border-border backdrop-blur-sm"
      data-testid={testId}>
      <div className="flex items-center gap-3">
        {room?.society?.logo ? (
          <img src={room.society.logo} alt="" className="w-12 h-12 rounded-full object-cover" />
        ) : (
          <div className="w-12 h-12 rounded-xl flex items-center justify-center text-base font-bold text-white"
            style={{ backgroundColor: room?.society?.primary_color || '#1a4d3e' }}>
            {room?.society?.name?.[0] || 'F'}
          </div>
        )}
        <div>
          <p className="text-sm font-heading font-semibold text-foreground">{room?.society?.name}</p>
          <p className="text-[10px] text-muted-foreground">{room?.tournament_name}</p>
        </div>
      </div>
    </motion.div>
  );
}

export function AuthStep({ room, email, setEmail, password, setPassword, firstName, setFirstName, lastName, setLastName,
  authMode, setAuthMode, authSubmitting, onSubmit, urlCode, setValidated, navigate }) {
  return (
    <motion.div key="auth-entry" {...ANIM_SLIDE_IN} transition={TRANSITION_FORM}>
      <SocietyBrandingBadge room={room} transition={TRANSITION_FORM_DELAYED} testId="society-branding-reveal" />

      <form onSubmit={onSubmit} className="space-y-3">
        {authMode === 'signup' && (
          <div className="grid grid-cols-2 gap-3">
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First name"
                className="h-12 bg-card border-border text-foreground pl-10 placeholder:text-muted-foreground focus-visible:ring-primary/50"
                data-testid="gatekeeper-auth-firstname-input" />
            </div>
            <div className="relative">
              <Input value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last name"
                className="h-12 bg-card border-border text-foreground pl-3 placeholder:text-muted-foreground focus-visible:ring-primary/50"
                data-testid="gatekeeper-auth-lastname-input" />
            </div>
          </div>
        )}
        <div className="relative">
          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address"
            className="h-12 bg-card border-border text-foreground pl-10 placeholder:text-muted-foreground focus-visible:ring-primary/50"
            data-testid="gatekeeper-auth-email-input" />
        </div>
        <div className="relative">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password"
            className="h-12 bg-card border-border text-foreground pl-10 placeholder:text-muted-foreground focus-visible:ring-primary/50"
            data-testid="gatekeeper-auth-password-input" />
        </div>
        {/* Cloudflare Turnstile widget removed — bot protection now handled
            at the Cloudflare WAF edge layer (Security → Bot Fight Mode). */}
        <Button type="submit" disabled={authSubmitting}
          className="w-full h-12 bg-primary hover:opacity-90 text-white font-semibold text-sm transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="gatekeeper-auth-submit-button">
          {authSubmitting
            ? <Loader2 className="w-5 h-5 animate-spin" />
            : authMode === 'signup' ? 'Create Account' : 'Sign In'}
        </Button>
      </form>
      <div className="mt-3 text-center">
        <button onClick={() => setAuthMode(authMode === 'login' ? 'signup' : 'login')}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="gatekeeper-auth-toggle">
          {authMode === 'login' ? (
            <>Don&apos;t have an account? <span className="text-teal-600">Sign up</span></>
          ) : (
            <>Already have an account? <span className="text-primary">Sign in</span></>
          )}
        </button>
      </div>
      {!urlCode && (
        <div className="mt-2 text-center">
          <button onClick={() => setValidated(false)}
            className="text-[11px] text-muted-foreground opacity-60 hover:opacity-100 transition-opacity"
            data-testid="gatekeeper-back-to-code">
            Have an access code? Go back
          </button>
        </div>
      )}
    </motion.div>
  );
}

export function EnterStep({ room, displayName, setDisplayName, onEnter }) {
  return (
    <motion.div key="name-entry" initial={ANIM_SLIDE_IN.initial} animate={ANIM_SLIDE_IN.animate} transition={TRANSITION_ENTER}>
      {room && (
        <motion.div {...ANIM_ICON_IN} transition={TRANSITION_ENTER_DELAYED}
          className="mb-6 p-5 rounded-2xl bg-card/80 border border-border backdrop-blur-sm"
          data-testid="society-branding-confirm">
          {room?.society?.logo ? (
            <img src={room.society.logo} alt="Society" className="w-16 h-16 rounded-full mx-auto mb-3 object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-xl mx-auto mb-3 flex items-center justify-center text-xl font-bold text-white"
              style={{ backgroundColor: room?.society?.primary_color || '#1a4d3e' }}>
              {room?.society?.name?.[0] || 'F'}
            </div>
          )}
          <h2 className="text-foreground font-heading text-lg font-semibold text-center">{room?.society?.name}</h2>
          <p className="text-muted-foreground text-sm mt-1 text-center">{room?.tournament_name}</p>
          <p className="text-muted-foreground text-xs mt-1 text-center">{room?.course}</p>
          <div className="flex items-center justify-center gap-4 mt-3 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{room?.start_date}</span>
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{room?.location}</span>
          </div>
          <div className="flex items-center justify-center gap-1 mt-2 text-xs text-muted-foreground">
            <Users className="w-3 h-3" /><span>{room?.entries_count || 0} players joined</span>
          </div>
        </motion.div>
      )}

      <div className="space-y-4">
        <div>
          <p className="text-xs text-muted-foreground mb-1.5">Display name for this pool</p>
          <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Your name"
            className="h-12 bg-card border-border text-foreground text-center placeholder:text-muted-foreground focus-visible:ring-primary/50"
            data-testid="gatekeeper-player-name-input" />
        </div>
        <Button onClick={onEnter} disabled={!displayName.trim()}
          className="w-full h-12 bg-primary hover:opacity-90 text-white font-semibold text-sm transition-opacity duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="gatekeeper-enter-room-button">
          Pick Your Team <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </motion.div>
  );
}
